import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { LiveBlogModalMode, LiveBlogModalView } from '../../state/types';
import { Spinner } from '../../../../moderation-v2/comments-and-posts/dashboard/components';
import { navigationItems } from './ModalHeader';

interface Props {
  mode: LiveBlogModalMode;
  view: LiveBlogModalView;
  isValidForm: boolean;
  isLoading: boolean;
  closeModal: () => void;
  onViewChange: (view: LiveBlogModalView) => void;
  createEvent: () => void;
  editEvent: () => void;
}

const ModalFooter: React.FC<Props> = ({
  mode,
  view,
  isValidForm,
  isLoading,
  closeModal,
  onViewChange,
  createEvent,
  editEvent
}) => {
  const [isPendingEvent, setIsPendingEvent] = useState<boolean>(false);
  const isEditMode = mode === LiveBlogModalMode.Edit;
  const disableCancel = !isEditMode && view === LiveBlogModalView.EmbedCode;
  const showPrevious =
    !isEditMode &&
    view !== navigationItems[0] &&
    view !== navigationItems[navigationItems.length - 1];
  const enableNext = !isEditMode && view !== navigationItems[navigationItems.length - 1];
  const isDisabled = !isValidForm;

  const onSave = () => {
    const isOnTeamTab = view === LiveBlogModalView.Team;

    // Edit Mode
    if (isEditMode) {
      setIsPendingEvent(true);
      editEvent();
      return;
    }

    // Create or Duplicate Mode
    if (isOnTeamTab) {
      setIsPendingEvent(true);
      createEvent();
    } else if (enableNext) {
      onNextClick();
    } else {
      closeModal();
    }
  };

  const onPreviousClick = () => {
    const currentIndex = navigationItems.findIndex((v: LiveBlogModalView) => v === view);
    const prevIndex = currentIndex - 1;

    if (prevIndex > -1 && prevIndex < navigationItems.length) {
      onViewChange(navigationItems[prevIndex]);
    }
  };

  const onNextClick = () => {
    const currentIndex = navigationItems.findIndex((v: LiveBlogModalView) => v === view);
    const nextIndex = currentIndex + 1;

    if (nextIndex > -1 && nextIndex < navigationItems.length) {
      onViewChange(navigationItems[nextIndex]);
    }
  };

  const onCancel = () => {
    closeModal();
  };

  const saveButtonText = useMemo(() => {
    if (view === LiveBlogModalView.EmbedCode && !isEditMode) {
      return 'Finish';
    } else {
      return `Save${enableNext ? ' & Continue' : ''}`;
    }
  }, [view, enableNext]);

  useEffect(() => {
    if (isPendingEvent && !isLoading) {
      setIsPendingEvent(false);
      if (!isEditMode) {
        onNextClick();
      }
    }
  }, [isLoading]);

  return (
    <div className={classNames('action-bar', showPrevious && 'with-previous')}>
      {showPrevious && <a onClick={onPreviousClick}>{`< Previous`}</a>}
      <div className={'action-buttons'}>
        {!disableCancel && (
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button className="save-button" onClick={onSave} disabled={isDisabled}>
          {isLoading ? <Spinner size={'small'} /> : saveButtonText}
        </button>
      </div>
    </div>
  );
};

export default ModalFooter;
